import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, CheckCircle2, ExternalLink, FileText, Home, Loader2, Star } from 'lucide-react';
import { answerLicenceHosting, submitLicenceSurvey } from '../api/candidate';
import {
  emptySurvey,
  safeReviewUrl,
  surveyPayload,
  surveyReady,
  type LicenceFollowup,
  type SurveyDraft,
} from '../lib/permis';

const QUERY_KEY = ['candidate', 'licence-followup'];

const errorText = (e: unknown, fallback: string) =>
  e && typeof e === 'object' && 'message' in e && (e as { message?: unknown }).message
    ? String((e as { message?: unknown }).message)
    : fallback;

function Choice({ pressed, onClick, disabled, children, label }: { pressed: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode; label?: string }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`min-w-[44px] px-3.5 py-2 rounded-lg border text-sm font-medium transition disabled:opacity-60 ${
        pressed ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300'
      }`}
    >
      {children}
    </button>
  );
}

/** Onglet « Mon permis » : félicitations, pièces pour la fabrication du permis, questionnaire et avis. */
export default function PermisView({ followup, onOpenDocuments }: { followup: LicenceFollowup; onOpenDocuments: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SurveyDraft>(emptySurvey);
  const definition = followup.survey.definition;
  const school = followup.schoolName || 'Votre auto-école';
  const reviews = { school: safeReviewUrl(followup.reviews.school), permio: safeReviewUrl(followup.reviews.permio) };

  const hosting = useMutation({
    mutationFn: answerLicenceHosting,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'document-requests'] });
    },
  });
  const survey = useMutation({
    mutationFn: () => submitLicenceSurvey(surveyPayload(definition, draft)),
    onSuccess: (data) => queryClient.setQueryData(QUERY_KEY, data),
  });
  const ready = surveyReady(definition, draft);

  return (
    <div className="space-y-6">
      <section className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 p-6 text-white shadow-lg">
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Award size={26} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">Félicitations{followup.firstName ? ` ${followup.firstName}` : ''} !</h1>
            <p className="mt-1 text-sm text-indigo-50">
              Toute l’équipe de {school} est fière de vous : vous avez obtenu votre permis de conduire. Encore bravo et bonne route !
            </p>
          </div>
        </div>
      </section>

      <section aria-labelledby="permis-pieces" className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 id="permis-pieces" className="font-semibold text-slate-900 flex items-center gap-2">
          <FileText size={18} className="text-indigo-600" aria-hidden="true" />
          Pièces pour la fabrication de votre permis
        </h2>
        <p className="mt-1 text-sm text-slate-600">Transmettez-les depuis « Mes documents » : votre auto-école s’occupe de la demande de fabrication.</p>
        <ul className="mt-3 space-y-1.5" aria-label="Pièces demandées">
          {followup.documents.map((doc) => (
            <li key={doc.docType} className="text-sm text-slate-800 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" aria-hidden="true" />
              {doc.label}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onOpenDocuments}
          className="mt-4 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
        >
          Déposer mes pièces
        </button>

        <div className="mt-6 pt-5 border-t border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Home size={16} className="text-indigo-600" aria-hidden="true" />
            Êtes-vous hébergé(e) chez un tiers ?
          </h3>
          {followup.hosted === true ? (
            <p className="mt-2 text-sm text-slate-600">
              Oui : {followup.hostDocuments.map((d) => d.label.charAt(0).toLowerCase() + d.label.slice(1)).join(', ')} ont été ajoutées à vos documents.
            </p>
          ) : (
            <>
              <p className="mt-1 text-xs text-slate-500">
                Si oui, nous vous demanderons aussi : {followup.hostDocuments.map((d) => d.label).join(', ')}.
              </p>
              <div className="mt-2.5 flex gap-2" role="group" aria-label="Êtes-vous hébergé(e) chez un tiers ?">
                <Choice pressed={false} disabled={hosting.isPending} onClick={() => hosting.mutate(true)}>
                  Oui
                </Choice>
                <Choice pressed={followup.hosted === false} disabled={hosting.isPending} onClick={() => hosting.mutate(false)}>
                  Non
                </Choice>
              </div>
            </>
          )}
          {hosting.isError && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {errorText(hosting.error, 'Réponse non enregistrée. Réessayez.')}
            </p>
          )}
        </div>
      </section>

      <section aria-labelledby="permis-avis" className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 id="permis-avis" className="font-semibold text-slate-900 flex items-center gap-2">
          <Star size={18} className="text-indigo-600" aria-hidden="true" />
          Votre avis sur votre formation
        </h2>
        {followup.survey.answeredAt ? (
          <p role="status" className="mt-3 flex items-center gap-2 text-sm text-emerald-700">
            <CheckCircle2 size={16} aria-hidden="true" />
            Merci pour vos réponses ! Elles aident {school} à progresser.
          </p>
        ) : (
          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) survey.mutate();
            }}
          >
            {definition.ratings.map((rating) => (
              <div key={rating.key} className="py-3 border-t border-slate-100 first:border-t-0 first:pt-0">
                <p className="text-sm font-medium text-slate-800 mb-2">{rating.label}</p>
                <div className="flex flex-wrap gap-2" role="group" aria-label={rating.label}>
                  {[1, 2, 3, 4, 5].map((note) => (
                    <Choice
                      key={note}
                      label={`${note} sur 5`}
                      pressed={draft.ratings[rating.key] === note}
                      onClick={() => setDraft((d) => ({ ...d, ratings: { ...d.ratings, [rating.key]: note } }))}
                    >
                      {note}
                    </Choice>
                  ))}
                </div>
              </div>
            ))}
            <div className="py-3 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-800 mb-2">{definition.recommend.label}</p>
              <div className="flex gap-2" role="group" aria-label={definition.recommend.label}>
                <Choice pressed={draft.recommend === true} onClick={() => setDraft((d) => ({ ...d, recommend: true }))}>
                  Oui
                </Choice>
                <Choice pressed={draft.recommend === false} onClick={() => setDraft((d) => ({ ...d, recommend: false }))}>
                  Non
                </Choice>
              </div>
            </div>
            <label className="block py-3 border-t border-slate-100">
              <span className="text-sm font-medium text-slate-800">{definition.comment.label}</span>
              <textarea
                value={draft.comment}
                maxLength={definition.comment.maxLength}
                rows={3}
                onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
                className="mt-2 w-full px-3.5 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
              />
            </label>
            {survey.isError && (
              <p role="alert" className="mb-3 text-sm text-red-600">
                {errorText(survey.error, 'Réponses non envoyées. Réessayez.')}
              </p>
            )}
            <button
              type="submit"
              disabled={!ready || survey.isPending}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {survey.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              Envoyer mes réponses
            </button>
          </form>
        )}
      </section>

      {(reviews.school || reviews.permio) && (
        <section aria-labelledby="permis-reviews" className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 id="permis-reviews" className="font-semibold text-slate-900">Partagez votre expérience</h2>
          <p className="mt-1 text-sm text-slate-600">Votre avis public aide les futurs élèves à choisir. Quelques secondes suffisent.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {reviews.school && (
              <a
                href={reviews.school}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
              >
                Laisser un avis Google à {school}
                <ExternalLink size={14} aria-hidden="true" />
              </a>
            )}
            {reviews.permio && (
              <a
                href={reviews.permio}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
              >
                Donner mon avis sur Permio
                <ExternalLink size={14} aria-hidden="true" />
              </a>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
